import { z } from "zod";
import {
  ControlDataTypeKey,
  Types,
  Schema
} from "../prop-controllers";
import { P, match } from "ts-pattern";
import { colorDataSchema, imageDataV0Schema, imageDataV1Schema } from "../data";
const colorBackgroundDataSchema = z.object({
  type: z.literal("color"),
  id: z.string(),
  payload: colorDataSchema.nullable()
});
const gradientStopDataSchema = z.object({
  id: z.string(),
  location: z.number(),
  color: colorDataSchema.nullable()
});
const gradientDataSchema = z.object({
  angle: z.number().optional(),
  isRadial: z.boolean().optional(),
  stops: z.array(gradientStopDataSchema)
});
const gradientBackgroundDataSchema = z.object({
  type: z.literal("gradient"),
  id: z.string(),
  payload: gradientDataSchema
});
const backgroundImagePositionDataSchema = z.object({
  x: z.number(),
  y: z.number()
});
const backgroundSizeDataSchema = z.union([
  z.literal("cover"),
  z.literal("contain"),
  z.literal("auto")
]);
const backgroundImageRepeatDataSchema = z.union([
  z.literal("no-repeat"),
  z.literal("repeat-x"),
  z.literal("repeat-y"),
  z.literal("repeat")
]);
const backgroundImageV0DataSchema = z.object({
  imageId: imageDataV0Schema,
  position: backgroundImagePositionDataSchema,
  size: backgroundSizeDataSchema.optional(),
  repeat: backgroundImageRepeatDataSchema.optional(),
  opacity: z.number().optional(),
  parallax: z.number().optional(),
  priority: z.boolean().optional()
});
const backgroundImageV1DataSchema = z.object({
  version: z.literal(1),
  image: imageDataV1Schema,
  position: backgroundImagePositionDataSchema,
  size: backgroundSizeDataSchema.optional(),
  repeat: backgroundImageRepeatDataSchema.optional(),
  opacity: z.number().optional(),
  parallax: z.number().optional(),
  priority: z.boolean().optional()
});
const backgroundImageDataSchema = z.union([
  backgroundImageV0DataSchema,
  backgroundImageV1DataSchema
]);
const imageBackgroundV0DataSchema = z.object({
  type: z.literal("image"),
  id: z.string(),
  payload: backgroundImageV0DataSchema
});
const imageBackgroundV1DataSchema = z.object({
  type: z.literal("image-v1"),
  id: z.string(),
  payload: backgroundImageV1DataSchema
});
const imageBackgroundDataSchema = z.union([
  imageBackgroundV0DataSchema,
  imageBackgroundV1DataSchema
]);
const backgroundVideoAspectRatioDataSchema = z.union([
  z.literal("wide"),
  z.literal("standard")
]);
const backgroundVideoDataSchema = z.object({
  url: z.string().optional(),
  maskColor: colorDataSchema.nullable().optional(),
  opacity: z.number().optional(),
  zoom: z.number().optional(),
  aspectRatio: backgroundVideoAspectRatioDataSchema.optional(),
  parallax: z.number().optional()
});
const videoBackgroundDataSchema = z.object({
  type: z.literal("video"),
  id: z.string(),
  payload: backgroundVideoDataSchema
});
const backgroundDataSchema = z.union([
  colorBackgroundDataSchema,
  gradientBackgroundDataSchema,
  imageBackgroundDataSchema,
  videoBackgroundDataSchema
]);
const responsiveBackgroundsDataSchema = Schema.responsiveValue(
  z.array(backgroundDataSchema)
);
const backgroundsPropControllerDataV1Schema = responsiveBackgroundsDataSchema;
const BackgroundsPropControllerDataV2Type = "prop-controllers::backgrounds::v2";
const backgroundsPropControllerDataV2Schema = z.object({
  [ControlDataTypeKey]: z.literal(BackgroundsPropControllerDataV2Type),
  value: responsiveBackgroundsDataSchema
});
const backgroundsPropControllerDataSchema = z.union([
  backgroundsPropControllerDataV1Schema,
  backgroundsPropControllerDataV2Schema
]);
function Backgrounds(options = {}) {
  return { type: Types.Backgrounds, version: 2, options };
}
function getBackgroundsPropControllerDataResponsiveBackgroundsData(data) {
  return match(data).with(
    { [ControlDataTypeKey]: BackgroundsPropControllerDataV2Type },
    (v2) => v2.value
  ).otherwise((v0) => v0);
}
function createBackgroundsPropControllerDataFromResponsiveBackgroundsData(data, definition) {
  return match(definition).with(
    { version: 2 },
    () => ({
      [ControlDataTypeKey]: BackgroundsPropControllerDataV2Type,
      value: data
    })
  ).otherwise(() => data);
}
function getBackgroundsPropControllerFileIds(data) {
  const value = getBackgroundsPropControllerDataResponsiveBackgroundsData(data);
  return value?.flatMap((override) => override.value).flatMap((backgroundItem) => {
    return match(backgroundItem).with(
      {
        type: "image-v1",
        payload: { image: { type: "makeswift-file" } }
      },
      (item) => [item.payload.image.id]
    ).with({ type: "image", payload: { imageId: P.string } }, (item) => [
      item.payload.imageId
    ]).otherwise(() => []);
  }) ?? [];
}
function getBackgroundsPropControllerSwatchIds(data) {
  const value = getBackgroundsPropControllerDataResponsiveBackgroundsData(data);
  return value?.flatMap((override) => override.value).flatMap((backgroundItem) => {
    switch (backgroundItem.type) {
      case "color":
        return backgroundItem.payload?.swatchId == null ? [] : [backgroundItem.payload.swatchId];
      case "gradient":
        return backgroundItem.payload.stops.flatMap(
          (stop) => stop.color == null ? [] : stop.color.swatchId
        );
      default:
        return [];
    }
  }) ?? [];
}
function copyResponsiveBackgroundsData(descriptor, data, context) {
  if (data == null)
    return data;
  return data.map((override) => ({
    ...override,
    value: override.value.map((backgroundItem) => {
      return match([descriptor, backgroundItem]).with([P.any, { type: "color" }], ([, item]) => {
        return {
          ...item,
          payload: item.payload === null ? null : {
            ...item.payload,
            swatchId: context.replacementContext.swatchIds.get(
              item.payload.swatchId
            ) ?? item.payload.swatchId
          }
        };
      }).with([P.any, { type: "gradient" }], ([, item]) => {
        return {
          ...item,
          payload: {
            ...item.payload,
            stops: item.payload.stops.map((stop) => ({
              ...stop,
              color: stop.color == null ? null : {
                ...stop.color,
                swatchId: context.replacementContext.swatchIds.get(
                  stop.color.swatchId
                ) ?? stop.color.swatchId
              }
            }))
          }
        };
      }).with(
        [
          { version: P.when((v) => v && v >= 1) },
          {
            type: "image-v1",
            payload: {
              version: 1,
              image: { type: "makeswift-file" }
            }
          }
        ],
        ([, item]) => {
          return {
            ...item,
            payload: {
              ...item.payload,
              image: {
                ...item.payload.image,
                id: context.replacementContext.fileIds.get(
                  item.payload.image.id
                ) ?? item.payload.image.id
              }
            }
          };
        }
      ).with(
        [P.any, { type: "image", payload: { imageId: P.string } }],
        ([, item]) => {
          return {
            ...item,
            payload: {
              ...item.payload,
              imageId: context.replacementContext.fileIds.get(
                item.payload.imageId
              ) ?? item.payload.imageId
            }
          };
        }
      ).otherwise(() => backgroundItem);
    })
  }));
}
function copyBackgroundsPropControllerData(descriptor, data, context) {
  return match(data).with(void 0, () => void 0).with(
    { [ControlDataTypeKey]: BackgroundsPropControllerDataV2Type },
    (v1) => {
      const value = copyResponsiveBackgroundsData(
        descriptor,
        v1.value,
        context
      );
      if (value == null)
        return void 0;
      return {
        [ControlDataTypeKey]: BackgroundsPropControllerDataV2Type,
        value
      };
    }
  ).otherwise((v0) => copyResponsiveBackgroundsData(descriptor, v0, context));
}
export {
  Backgrounds,
  BackgroundsPropControllerDataV2Type,
  backgroundImageDataSchema,
  backgroundsPropControllerDataSchema,
  copyBackgroundsPropControllerData,
  createBackgroundsPropControllerDataFromResponsiveBackgroundsData,
  getBackgroundsPropControllerDataResponsiveBackgroundsData,
  getBackgroundsPropControllerFileIds,
  getBackgroundsPropControllerSwatchIds,
  imageBackgroundDataSchema
};
//# sourceMappingURL=backgrounds.js.map